import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { MapPin, Navigation, X } from 'lucide-react';

import { loadGoogleMaps } from '../../utils/googleMaps';

const coordinates = (place) => {
  if (place?.latitude == null || place?.longitude == null) return null;
  return { lat: Number(place.latitude), lng: Number(place.longitude) };
};

export default function GooglePlacePicker({
  label,
  value,
  onChange,
  disabled = false,
  allowCurrentLocation = false,
  editableName = false,
}) {
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [loadError, setLoadError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [locating, setLocating] = useState(false);
  const selectedCoordinates = useMemo(
    () => coordinates(value),
    [value?.latitude, value?.longitude],
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let alive = true;
    let autocomplete = null;
    let selectHandler = null;

    loadGoogleMaps()
      .then(async (maps) => {
        const { PlaceAutocompleteElement } = await maps.importLibrary('places');
        if (!alive || !autocompleteRef.current) return;
        autocomplete = new PlaceAutocompleteElement();
        autocomplete.placeholder = `Search for ${label.toLowerCase()}`;
        autocomplete.setAttribute('aria-label', label);
        autocomplete.style.width = '100%';
        if (disabled) autocomplete.setAttribute('disabled', '');
        selectHandler = async ({ placePrediction }) => {
          try {
            const place = placePrediction.toPlace();
            await place.fetchFields({
              fields: ['id', 'displayName', 'formattedAddress', 'location'],
            });
            if (!alive || !place.location) return;
            onChangeRef.current({
              name: place.displayName || null,
              address: place.formattedAddress || null,
              place_id: place.id || null,
              latitude: place.location.lat(),
              longitude: place.location.lng(),
            });
            setLoadError('');
            setLocationError('');
          } catch {
            if (alive) setLoadError('Google Maps could not load that place. Try again.');
          }
        };
        autocomplete.addEventListener('gmp-select', selectHandler);
        autocompleteRef.current.replaceChildren(autocomplete);
      })
      .catch((error) => {
        if (alive) setLoadError(error.message || 'Google Maps could not load.');
      });

    return () => {
      alive = false;
      if (autocomplete && selectHandler) {
        autocomplete.removeEventListener('gmp-select', selectHandler);
      }
      if (autocompleteRef.current) autocompleteRef.current.replaceChildren();
    };
  }, [disabled, label]);

  useEffect(() => {
    let alive = true;
    if (!selectedCoordinates || !mapRef.current) return undefined;
    loadGoogleMaps()
      .then(async (maps) => {
        await maps.importLibrary('maps');
        if (!alive || !mapRef.current) return;
        const map = new maps.Map(mapRef.current, {
          center: selectedCoordinates,
          zoom: 15,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        if (maps.Marker) {
          new maps.Marker({ map, position: selectedCoordinates, title: value?.name || label });
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [label, selectedCoordinates, value?.name]);

  const useCurrentLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationError('This browser does not support current location.');
      return;
    }

    setLocating(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        onChangeRef.current({
          name: 'Current location',
          address: null,
          place_id: null,
          latitude: coords.latitude,
          longitude: coords.longitude,
        });
        setLocating(false);
      },
      (error) => {
        const denied = error?.code === error?.PERMISSION_DENIED || error?.code === 1;
        setLocationError(
          denied
            ? 'Location permission was denied. Allow location access in your browser settings and try again.'
            : 'Your current location could not be found. Try again or search for a starting location.',
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  return (
    <Stack spacing={1.25} alignItems="stretch">
      <Typography variant="body2" sx={{ color: 'primary.main', textAlign: 'left' }}>
        {label}
      </Typography>
      <Box
        ref={autocompleteRef}
        sx={(theme) => ({
          minHeight: 48,
          p: 0.5,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.55)}`,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          '& gmp-place-autocomplete': { width: '100%' },
        })}
      />
      {allowCurrentLocation && (
        <Button
          type="button"
          variant="outlined"
          startIcon={<Navigation size={17} />}
          disabled={disabled || locating}
          onClick={useCurrentLocation}
          sx={{ alignSelf: 'flex-start', color: 'primary.main', borderColor: 'primary.main' }}
        >
          {locating ? 'Finding location...' : 'Use my current location'}
        </Button>
      )}
      {loadError && <Alert severity="error" sx={{ color: 'secondary.main' }}>{loadError}</Alert>}
      {locationError && (
        <Alert severity="error" sx={{ color: 'secondary.main' }}>{locationError}</Alert>
      )}
      {selectedCoordinates && (
        <Box
          ref={mapRef}
          aria-label={`${label} map`}
          sx={(theme) => ({
            width: '100%',
            height: 220,
            borderRadius: 4,
            overflow: 'hidden',
            border: `2px solid ${theme.palette.primary.main}`,
          })}
        />
      )}
      {(value?.name || value?.address) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={(theme) => ({
            p: 1.5,
            borderRadius: 3,
            border: `1px solid ${theme.palette.green.main}`,
            bgcolor: alpha(theme.palette.green.main, 0.06),
          })}
        >
          {editableName ? (
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                size="small"
                label="Location name"
                value={value.name || ''}
                disabled={disabled}
                inputProps={{ maxLength: 255 }}
                onChange={(inputEvent) => onChangeRef.current({
                  ...value,
                  name: inputEvent.target.value,
                })}
              />
              <TextField
                size="small"
                label="Address"
                value={value.address || ''}
                inputProps={{ readOnly: true }}
              />
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <MapPin size={19} />
              <Box sx={{ minWidth: 0 }}>
                {value.name && (
                  <Typography variant="body2" sx={{ color: 'primary.main', textAlign: 'left' }}>
                    {value.name}
                  </Typography>
                )}
                {value.address && (
                  <Typography variant="caption" sx={{ color: 'secondary.main', textAlign: 'left' }}>
                    {value.address}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
          <Button
            type="button"
            size="small"
            startIcon={<X size={15} />}
            disabled={disabled}
            onClick={() => onChangeRef.current(null)}
            sx={{ color: 'secondary.main', flexShrink: 0 }}
          >
            Clear
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
